import { FieldValues, FormProvider, Path, useFormContext, UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import DateRangePicker from "../form/dateRangePicker.comp";
import { FancyCheckbox } from "../reuseables/form.comp";
import XMarkIcon from "../../icons/xMark.icon";
import ArrowRightIcon from "../../icons/arrowRight.icon";
import AdjustmentsHorizontalIcon from "../../icons/adjustmentsHorizontal.icon";
import Modal from "../reuseables/modal.comp";
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { DeliveryListFilter } from "../../types/delivery";
import useHKDistricts from "../../hooks/useHKDistricts.hook";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { inputCssNoMy } from "../reuseables/native.comp";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import productCore from "../../core/product.core";
import warehouseCore from "../../core/warehouse.core";
import suplierCore from "../../core/supplier.core";
import localeCore from "../../core/locale.core";
import shipCore from "../../core/ship.core";

export default function DeliveriesFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const [modalOpened, setModalOpened] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <section data-type="section">
      <FilterBar btnRef={btnRef} setModalOpened={setModalOpened} methods={methods} />
      <FilterModal btnRef={btnRef} modalOpened={modalOpened} setModalOpened={setModalOpened} methods={methods} />
    </section>
  );
}

function FilterModal({
  btnRef,
  modalOpened,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLButtonElement>;
  modalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<DeliveryListFilter, any>;
}) {
  const { t, msg } = useTranslation();

  if (!modalOpened) return null;

  return (
    <Modal
      btnRef={btnRef}
      setModalOpened={setModalOpened}
      cssHeight="top-[20dvh] md:top-1/2 h-[60dvh] md:h-[600px]  overflow-scroll "
    >
      <div className="flex gap-8 flex-wrap py-14 px-8">
        <h1 className="basis-full text-center text-xl">{t(msg.order.orderListFilter)}</h1>
        <ProductFilter methods={methods} />
        <HKRegionFilter methods={methods} />
        <div className="basis-full flex gap-2">
          <WarehouseFilter methods={methods} />
          <SuppliersFilter methods={methods} />
        </div>
        <ShipOptionsFilter methods={methods} />
        <div className="block border p-2">
          <DateRangePicker methods={methods} />
        </div>
      </div>
    </Modal>
  );
}

function FilterBar({
  btnRef,
  setModalOpened,
  methods,
}: {
  btnRef: RefObject<HTMLButtonElement>;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  methods: UseFormReturn<DeliveryListFilter, any>;
}) {
  return (
    <button
      ref={btnRef}
      className="border rounded-lg py-2 px-4 items-center flex justify-between w-full"
      onClick={() => setModalOpened(true)}
    >
      <div className="flex flex-wrap gap-2">
        <DateOptionItem methods={methods} />
        <DateRange methods={methods} />
        <ProductFilterBadges methods={methods} />
        <WarehouseFilterBadges methods={methods} />
        <SupplierFilterBadges methods={methods} />
        <RegionFilterBadges methods={methods} />
        <DistrictFilterBadges methods={methods} />
        <SubDistrictFilterBadges methods={methods} />
        <ShipOptionFilterBadges methods={methods} />
      </div>
      <AdjustmentsHorizontalIcon />
    </button>
  );
}

function DateOptionItem({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { t, msg } = useTranslation();
  const dateOption = useWatch({ control: methods.control, name: "dateOption" });

  function getText() {
    switch (dateOption) {
      case "last7Days":
        return t(msg.date.last7Days);
      case "today":
        return t(msg.date.today);
      case "last30Days":
        return t(msg.date.last30Days);
      case "last365Days":
        return t(msg.date.last365Days);
      default:
        return "";
    }
  }

  if (!dateOption) return null;

  return <Badge bgColor="bg-admin-blue">{getText()}</Badge>;
}

function DateRange({ methods }: { methods: UseFormReturn<any, any> }) {
  const [date, dateOption] = useWatch({ control: methods.control, name: ["date", "dateOption"] });

  if (dateOption) return null;

  return (
    <Badge bgColor="bg-admin-blue">
      {date.from} <ArrowRightIcon /> {date.to}
    </Badge>
  );
}

function ProductFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const { ps } = router.query;

  const { data: products } = useQuery({
    queryKey: [ps],
    queryFn: (context) => productCore.searchProduct(context.queryKey[0] as string),
    enabled: ps !== "",
  });

  const productIds = useWatch({ control: methods.control, name: "productIds" }) || [];

  if (!productIds || productIds.length === 0) return null;

  const selectedProducts = products?.list.filter((p) => productIds.includes(p.id)) || [];

  const clearProduct = (productId: string) => {
    const currentIds = (methods.getValues("productIds") || []) as string[];
    (methods.setValue as any)(
      "productIds",
      currentIds.filter((id) => id !== productId)
    );
  };

  return (
    <>
      {selectedProducts.map((product) => (
        <Badge key={product.id} bgColor="bg-blue-500" onClose={() => clearProduct(product.id)}>
          {localeCore.getLocaleValueDefaultFallback(product.name)}
        </Badge>
      ))}
    </>
  );
}

function WarehouseFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const { data: warehouses } = useQuery({
    queryKey: ["delivery-warehouses"],
    queryFn: (context) => warehouseCore.list(),
    enabled: router.isReady,
  });

  const warehouseIds = useWatch({ control: methods.control, name: "warehouseIds" }) || [];

  if (!warehouseIds || warehouseIds.length === 0) return null;

  const selectedWarehouses = warehouses?.filter((w) => warehouseIds.includes(w.id)) || [];

  const clearWarehouse = (warehouseId: string) => {
    const currentIds = (methods.getValues("warehouseIds") || []) as string[];
    (methods.setValue as any)(
      "warehouseIds",
      currentIds.filter((id) => id !== warehouseId)
    );
  };

  return (
    <>
      {selectedWarehouses.map((warehouse) => (
        <Badge key={warehouse.id} bgColor="bg-purple-500" onClose={() => clearWarehouse(warehouse.id)}>
          {warehouse.name}
        </Badge>
      ))}
    </>
  );
}

function SupplierFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const { data: suppliers } = useQuery({
    queryKey: ["delivery-suppliers"],
    queryFn: (context) => suplierCore.list(),
    enabled: router.isReady,
  });

  const supplierIds = useWatch({ control: methods.control, name: "supplierIds" }) || [];

  if (!supplierIds || supplierIds.length === 0) return null;

  const selectedSuppliers = suppliers?.filter((s) => supplierIds.includes(s.id)) || [];

  const clearSupplier = (supplierId: string) => {
    const currentIds = (methods.getValues("supplierIds") || []) as string[];
    (methods.setValue as any)(
      "supplierIds",
      currentIds.filter((id) => id !== supplierId)
    );
  };

  return (
    <>
      {selectedSuppliers.map((supplier) => (
        <Badge key={supplier.id} bgColor="bg-orange-500" onClose={() => clearSupplier(supplier.id)}>
          {supplier.name}
        </Badge>
      ))}
    </>
  );
}

function RegionFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const regions = useWatch({ control: methods.control, name: "regions" }) || [];

  if (!regions || regions.length === 0) return null;

  const clearRegion = (region: string) => {
    const currentRegions = (methods.getValues("regions") || []) as string[];
    (methods.setValue as any)(
      "regions",
      currentRegions.filter((r) => r !== region)
    );
  };

  return (
    <>
      {regions.map((region) => (
        <Badge key={region} bgColor="bg-emerald-500" onClose={() => clearRegion(region)}>
          {region}
        </Badge>
      ))}
    </>
  );
}

function DistrictFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const districts = useWatch({ control: methods.control, name: "districts" }) || [];

  if (!districts || districts.length === 0) return null;

  const clearDistrict = (district: string) => {
    const currentDistricts = (methods.getValues("districts") || []) as string[];
    (methods.setValue as any)(
      "districts",
      currentDistricts.filter((d) => d !== district)
    );
  };

  return (
    <>
      {districts.map((district) => (
        <Badge key={district} bgColor="bg-teal-500" onClose={() => clearDistrict(district)}>
          {district}
        </Badge>
      ))}
    </>
  );
}

function SubDistrictFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { data: subDistricts } = useHKDistricts();
  const subDistrictIds = useWatch({ control: methods.control, name: "subDistricts" }) || [];

  if (!subDistrictIds || subDistrictIds.length === 0) return null;

  const selectedSubDistricts = subDistricts?.filter((sd) => subDistrictIds.includes(sd.id)) || [];

  const clearSubDistrict = (subDistrictId: string) => {
    const currentIds = (methods.getValues("subDistricts") || []) as string[];
    (methods.setValue as any)(
      "subDistricts",
      currentIds.filter((id) => id !== subDistrictId)
    );
  };

  return (
    <>
      {selectedSubDistricts.map((subDistrict) => (
        <Badge key={subDistrict.id} bgColor="bg-cyan-500" onClose={() => clearSubDistrict(subDistrict.id)}>
          {subDistrict.subDistrict}
        </Badge>
      ))}
    </>
  );
}

function ShipOptionFilterBadges({ methods }: { methods: UseFormReturn<DeliveryListFilter, any> }) {
  const { t, msg } = useTranslation();
  const router = useRouter();

  const { data: shipSettings } = useQuery({
    queryKey: ["delivery-shipOptions"],
    queryFn: () => shipCore.getShipSettings(),
    enabled: router.isReady,
  });

  const shipOptionIds = useWatch({ control: methods.control, name: "shipOptionIds" }) || [];

  if (!shipOptionIds || shipOptionIds.length === 0) return null;

  const allShipOptions = shipSettings?.flatMap((s) => s.options) || [];
  const selectedShipOptions = allShipOptions.filter((so) => shipOptionIds.includes(so.id));

  const clearShipOption = (shipOptionId: string) => {
    const currentIds = (methods.getValues("shipOptionIds") || []) as string[];
    (methods.setValue as any)(
      "shipOptionIds",
      currentIds.filter((id) => id !== shipOptionId)
    );
  };

  return (
    <>
      {selectedShipOptions.map((shipOption) => (
        <Badge key={shipOption.id} bgColor="bg-indigo-500" onClose={() => clearShipOption(shipOption.id)}>
          {shipOption.name}
        </Badge>
      ))}
    </>
  );
}

function ProductFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter> }) {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const { ps } = router.query;

  const { data: products } = useQuery({
    queryKey: [ps],
    queryFn: (context) => productCore.searchProduct(context.queryKey[0] as string),
    enabled: ps !== "",
  });

  const setSearchText = (text: string) => {
    router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        ps: text,
      },
    });
  };

  const productOptions =
    products?.list.map((p) => ({ label: localeCore.getLocaleValueDefaultFallback(p.name), value: p.id })) ?? [];

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.product.filterProducts)}</h2>
      <MultipleSelectorComp
        fieldName="productIds"
        options={productOptions}
        methods={methods}
        labelMaxLength={20}
        onSearch={setSearchText}
      />
    </div>
  );
}

function WarehouseFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter> }) {
  const router = useRouter();
  const { t, msg } = useTranslation();

  const { data: warehouses } = useQuery({
    queryKey: ["delivery-warehouses"],
    queryFn: (context) => warehouseCore.list(),
    enabled: router.isReady,
  });

  const warehouseOptions = warehouses?.map((w) => ({ label: w.name, value: w.id })) ?? [];

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.posMenuItems.warehouse)}</h2>
      <MultipleSelectorComp fieldName="warehouseIds" options={warehouseOptions} methods={methods} labelMaxLength={20} />
    </div>
  );
}

function SuppliersFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter> }) {
  const router = useRouter();
  const { t, msg } = useTranslation();

  const { data: suppliers } = useQuery({
    queryKey: ["delivery-suppliers"],
    queryFn: (context) => suplierCore.list(),
    enabled: router.isReady,
  });

  const supplierOptions = suppliers?.map((w) => ({ label: w.name, value: w.id })) ?? [];

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.inventory.supplier)}</h2>
      <MultipleSelectorComp fieldName="supplierIds" options={supplierOptions} methods={methods} labelMaxLength={20} />
    </div>
  );
}

function HKRegionFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter> }) {
  const { t, msg } = useTranslation();
  const { data: subDistricts, isPending, isError } = useHKDistricts();

  const [selectedRegions, selectedDistricts] = useWatch({
    control: methods.control,
    name: ["regions", "districts"],
  }) as [string[], string[]];

  const distinctRegions = useMemo(() => {
    if (!subDistricts) return [];
    return [...new Set(subDistricts.map((s) => s.region))].map((r) => {
      return { label: r, value: r };
    });
  }, [subDistricts]);

  const distinctDistricts = useMemo(() => {
    if (!subDistricts) return [];

    const filteredDistricts = [...new Set(subDistricts.filter((s) => selectedRegions.includes(s.region)).map((s) => s.district))];
    return filteredDistricts.map((r) => {
      return { label: r, value: r };
    });
  }, [subDistricts, selectedRegions]);

  const distinctSubdistricts = useMemo(() => {
    if (!subDistricts) return [];
    const filteredSubDistricts = [
      ...new Set(subDistricts.filter((d) => selectedRegions.includes(d.region) && selectedDistricts.includes(d.district))),
    ];

    return filteredSubDistricts.map((s) => {
      return { label: s.subDistrict, value: s.id };
    });
  }, [subDistricts, selectedRegions, selectedDistricts]);

  if (isError) return <h2>Error Loading Districts...</h2>;
  if (isPending) return <LoadingOvalCenterIcon />;

  return (
    <div className="basis-full">
      <h2>{t(msg.product.filterLocation)}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3  gap-2">
        <MultipleSelectorComp fieldName="regions" options={distinctRegions} methods={methods} labelMaxLength={20} />
        <MultipleSelectorComp fieldName="districts" options={distinctDistricts} methods={methods} labelMaxLength={20} />
        <MultipleSelectorComp fieldName="subDistricts" options={distinctSubdistricts} methods={methods} labelMaxLength={20} />
      </div>
    </div>
  );
}

// admin-green
// admin-blue
function Badge({ bgColor, children, onClose }: { bgColor: string; children: React.ReactNode; onClose?: () => void }) {
  return (
    <div className={`${bgColor} text-white px-4 py-2 rounded-full inline-flex gap-2`}>
      {children}
      {onClose && (
        <button
          className="fill-white"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

interface SelectorOption {
  label: string;
  value: string;
}

function MultipleSelectorComp<T extends FieldValues>({
  methods,
  fieldName,
  labelMaxLength = 30,
  options,
  onSearch,
}: {
  methods: UseFormReturn<T, any>;
  fieldName: Path<T>;
  labelMaxLength?: number;
  options: SelectorOption[];
  onSearch?: (text: string) => void;
}) {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      // This is a hack to get the latest state
      if (!ref || !ref.current) return;
      const isOpened = (ref.current as any).getAttribute("data-state");

      if (!isOpened) return;

      const isWithin = (e.target as any).closest(`#multiple-select-${fieldName}`);

      if (!isWithin) {
        setIsOpened(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
    // eslint-disable-next-line
  }, []);

  const css =
    "relative " +
    "h-9 w-full " +
    "dark:bg-her-backblue " +
    `border rounded-t-md ${
      !isOpened ? "rounded-b-md" : ""
    } hover:border-blue-200 hover:border-2 focus-visible:border-blue-600 focus-visible:border-2 ` +
    "focus-visible:outline-none outline-none " +
    "focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "duration-1000 transition-[box-shadow] " +
    "disabled:text-her-lightgray2 text-base ";

  const values: string[] = useWatch({ control: methods.control, name: fieldName }) ?? [];
  const names = options.filter((o) => values.includes(o.value)).map((p) => p.label);

  return (
    <FormProvider {...methods}>
      <div id={`multiple-select-${fieldName}`} ref={ref} data-state={isOpened} className={css}>
        <button
          type="button"
          className="w-full h-full flex justify-between pr-2 pl-4 items-center"
          onClick={() => setIsOpened((value) => !value)}
        >
          <CurrentName names={names} labelMaxLength={labelMaxLength} /> <ChevronDownIcon />
        </button>
        {isOpened && <Panel fieldName={fieldName} options={options} onSearch={onSearch} />}
      </div>
    </FormProvider>
  );
}

function CurrentName({ names, labelMaxLength }: { names: string[]; labelMaxLength: number }) {
  let text = names.join(", ");

  if (text.length > labelMaxLength) {
    text = text.substring(0, labelMaxLength - 3) + " ...";
  }

  return <div>{text}</div>;
}

function Panel<T extends FieldValues>({
  options,
  fieldName,
  onSearch,
}: {
  options: SelectorOption[];
  fieldName: Path<T>;
  onSearch?: (text: string) => void;
}) {
  return (
    <div className="brand_panel absolute top-[34px] left-0 w-full border rounded-b-md hover:border-blue-200 bg-white dark:bg-her-backblue space-y-1 px-4 py-4 max-h-72 overflow-y-auto z-50">
      {onSearch ? <SearchBox onSearch={onSearch} /> : null}
      {options.map((c) => (
        <PanelRow key={c.label} fieldName={fieldName} option={c} />
      ))}
    </div>
  );
}

function PanelRow<T extends FieldValues>({ option, fieldName }: { option: SelectorOption; fieldName: Path<T> }) {
  const { register } = useFormContext();
  return (
    <div className="w-full text-left">
      <FancyCheckbox value={option.value} register={register(fieldName)}>
        {option.label}
      </FancyCheckbox>
    </div>
  );
}

const SearchBox = ({ onSearch }: { onSearch: (text: string) => void }) => {
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLInputElement>(null);
  const { t, msg } = useTranslation();

  function click() {
    onSearch(searchText);
    setSearchText("");

    if (ref.current) {
      ref.current.value = "";
    }
  }

  return (
    <div className="flex gap-4 items-center">
      <input
        id="product-search"
        ref={ref}
        className={`${inputCssNoMy} grow`}
        placeholder={t(msg.product.searchProduct)}
        type="text"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            click();
          }
        }}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
      />

      <button
        id="search-delivery-product"
        className="bg-her-dodgerblue text-white px-4 py-1 rounded-md h-9"
        onClick={(e) => {
          e.preventDefault();
          click();
        }}
        type="button"
      >
        {t(msg.general.search)}
      </button>
    </div>
  );
};

function ShipOptionsFilter({ methods }: { methods: UseFormReturn<DeliveryListFilter> }) {
  const router = useRouter();
  const { t, msg } = useTranslation();

  const { data: shipSettings } = useQuery({
    queryKey: ["delivery-shipOptions"],
    queryFn: () => shipCore.getShipSettings(),
    enabled: router.isReady,
  });

  const shipSettingOptions = shipSettings?.flatMap((s) => s.options)?.map((w) => ({ label: w.name, value: w.id })) ?? [];

  return (
    <div className="basis-full gap-2">
      <h2>{t(msg.ship.shipSettingOption)}</h2>
      <MultipleSelectorComp fieldName="shipOptionIds" options={shipSettingOptions} methods={methods} labelMaxLength={40} />
    </div>
  );
}
